import { RSAA } from 'redux-api-middleware'
import { pathOr, uniq } from 'ramda'
import {
  getApiPath,
  makeQueryParams,
  makePaginationParams,
} from 'sidecar-utility-library'

import { types, initialState } from './types'
import { parseCareDetail } from './utils'

export const fetchPublicCareDetail = ({
  careEpisode = false,
  category,
  filingUuid,
  gsn,
  isInitialLoad = false,
  qty,
  uuid,
  zip,
}) => ({
  [RSAA]: {
    endpoint: () => {
      return getApiPath(
        'care',
        `cares/pub/detail${makeQueryParams({
          careEpisode,
          category,
          gsn: gsn,
          insuranceFilingUuid: filingUuid,
          // This boolean is meant to determine whether or not a call to the cares/details endpoint is
          // the first one on initial page load, or a subsequent call. This is because script save
          // requires us to call it twice to get the information we need, once without an NPI number,
          // and then once we have the npi # from the first call, calling it again with the correct #
          // to get the rest of the information about the prescription.
          prescriptionInitialLoad: isInitialLoad,
          qty: qty,
          uuid,
          zipCode: zip,
        })}`,
      )
    },
    method: 'GET',
    types: [
      types.FETCH_PUBLIC_CARE_DETAIL_REQUEST,
      types.FETCH_PUBLIC_CARE_DETAIL_SUCCESS,
      types.FETCH_PUBLIC_CARE_DETAIL_ERROR,
    ],
    cache: true,
  },
})

export const fetchPublicCareSearch = ({ filingUuid, query, zip }) => ({
  [RSAA]: {
    endpoint: ({ Care }) => {
      // We don't bother encoding the query, but probably should
      // but the backend won't accept it though.
      return getApiPath(
        'care',
        `cares/pub/search${makeQueryParams({
          insuranceFilingUuid: filingUuid,
          query,
          zip,
          ...makePaginationParams(Care?.publicSearchResults, {}),
        })}`,
      )
    },
    method: 'GET',
    types: [
      types.FETCH_PUBLIC_CARE_SEARCH_REQUEST,
      types.FETCH_PUBLIC_CARE_SEARCH_SUCCESS,
      types.FETCH_PUBLIC_CARE_SEARCH_ERROR,
    ],
    cache: true,
  },
})

export const resetPublicCareSearch = () => ({
  type: types.RESET_PUBLIC_CARE_SEARCH,
})

export const resetPublicCareDetail = () => ({
  type: types.RESET_PUBLIC_CARE_DETAIL,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    /** Public Care Search */
    case types.FETCH_PUBLIC_CARE_SEARCH_REQUEST: {
      return {
        ...state,
        publicSearchResults: {
          ...state.publicSearchResults,
          busy: true,
          error: '',
        },
      }
    }
    case types.FETCH_PUBLIC_CARE_SEARCH_SUCCESS: {
      const { content, page, last } = payload
      const isLastPageReached = last || content.length === 0
      const mergedContent = uniq([
        ...(state.publicSearchResults?.entities || []),
        ...content,
      ])
      return {
        ...state,
        publicSearchResults: {
          ...state.publicSearchResults,
          page,
          busy: false,
          error: '',
          isLastPageReached,
          entities: mergedContent,
        },
      }
    }
    case types.FETCH_PUBLIC_CARE_SEARCH_ERROR: {
      return {
        ...state,
        publicSearchResults: {
          ...state.publicSearchResults,
          busy: false,
          error: pathOr(
            'Could not fetch search',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }
    case types.RESET_PUBLIC_CARE_SEARCH: {
      return {
        ...state,
        publicSearchResults: {
          ...state.publicSearchResults,
          busy: false,
          error: '',
          // page must be null in order to be reset to 0 on next fetch
          page: null,
          isLastPageReached: false,
          entities: [],
        },
      }
    }

    /** Public Care Detail */
    case types.FETCH_PUBLIC_CARE_DETAIL_REQUEST: {
      return {
        ...state,
        publicDetail: {
          ...state.publicDetail,
          busy: true,
          error: null,
        },
      }
    }
    case types.FETCH_PUBLIC_CARE_DETAIL_SUCCESS: {
      return {
        ...state,
        publicDetail: {
          ...state.publicDetail,
          busy: false,
          error: null,
          entity: parseCareDetail(payload),
        },
      }
    }
    case types.FETCH_PUBLIC_CARE_DETAIL_ERROR: {
      return {
        ...state,
        publicDetail: {
          ...state.publicDetail,
          busy: false,
          error: pathOr(
            'Could not fetch detail',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }
    case types.RESET_PUBLIC_CARE_DETAIL: {
      return {
        ...state,
        publicDetail: {
          ...state.publicDetail,
          busy: false,
          error: null,
          entity: {},
        },
      }
    }

    default:
      return state
  }
}
